import { useMemo } from 'react';
import { MARKETS, type MarketCode } from '@hc/shared';
import { useBuyerMandates } from '../api/hooks.js';

// Mandate-driven panel defaults. Market-scoped panels (Prevalence, Indications,
// Map) open on the buyer's mandated markets instead of a hardcoded UK default:
// the mandate is the platform's statement of what the user is building, so it
// is the right opening scope. When the user has several mandates the most
// recently updated one wins. No mandate (or a mandate with no markets) returns
// an empty list — callers fall back to their own neutral default (Europe-wide,
// or a random market).
//
// `loaded` distinguishes "no mandate" from "mandates not fetched yet" so a
// panel can adopt the default exactly once, without flashing a fallback first.

const MARKET_SET: ReadonlySet<string> = new Set(MARKETS);

export function useMandateCountries(): { countries: MarketCode[]; loaded: boolean } {
  const { data, isSuccess, isError } = useBuyerMandates();
  const countries = useMemo(() => {
    const mandates = data?.data ?? [];
    const withCountries = mandates
      .filter((m) => m.countries.some((c) => MARKET_SET.has(c)))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
    const top = withCountries[0];
    if (!top) return [];
    return top.countries.filter((c): c is MarketCode => MARKET_SET.has(c));
  }, [data]);
  return { countries, loaded: isSuccess || isError };
}
